/**
 * RUMI quiz engine — the state machine behind sections/quiz-app.liquid.
 *
 * All six steps are in the DOM; this shows one at a time, validates it, mirrors
 * the answers into sessionStorage and finally hands off to the results page.
 *
 * Scoring, concern slugs and URL encoding all live in rumi-quiz-logic.js, the
 * contract shared with the results page. Nothing here may reimplement them.
 */
(function () {
  'use strict';

  var Logic = window.RumiQuiz;
  var root = document.querySelector('[data-rumi-quiz]');
  if (!Logic || !root) return;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var config = {
    resultsUrl: root.dataset.resultsUrl || '/pages/quiz-results',
    exitUrl: root.dataset.exitUrl || '/',
    total: parseInt(root.dataset.totalSteps, 10) || 6,
    weightMin: parseInt(root.dataset.weightMin, 10) || 1,
    weightMax: parseInt(root.dataset.weightMax, 10) || 120,
    weightDefault: parseInt(root.dataset.weightDefault, 10) || 30,
    unitKg: root.dataset.unitKg || 'kg',
    unitLb: root.dataset.unitLb || 'lb',
    concernsRequired: root.dataset.concernsRequired === 'true',
    announce: root.dataset.announce || 'Step [step] of [total]',
    klaviyoKey: root.dataset.klaviyoKey || '',
    klaviyoList: root.dataset.klaviyoList || ''
  };

  var el = {
    form: root.querySelector('[data-quiz-form]'),
    steps: Array.prototype.slice.call(root.querySelectorAll('[data-quiz-step]')),
    back: root.querySelector('[data-quiz-back]'),
    next: root.querySelector('[data-quiz-next]'),
    nextLabel: root.querySelector('[data-quiz-next-label]'),
    fill: root.querySelector('[data-quiz-fill]'),
    pct: root.querySelector('[data-quiz-pct]'),
    live: root.querySelector('[data-quiz-live]'),
    name: root.querySelector('[data-quiz-name]'),
    breed: root.querySelector('[data-quiz-breed]'),
    email: root.querySelector('[data-quiz-email]'),
    weight: root.querySelector('[data-quiz-weight]'),
    readout: root.querySelector('[data-quiz-readout]'),
    dogs: Array.prototype.slice.call(root.querySelectorAll('[data-quiz-dog]')),
    day: root.querySelector('[data-quiz-day]'),
    month: root.querySelector('[data-quiz-month]'),
    year: root.querySelector('[data-quiz-year]'),
    chips: root.querySelector('[data-quiz-chips]')
  };

  var answers = {
    name: '',
    weightKg: config.weightDefault,
    dob: '',
    breed: '',
    concerns: [],
    email: ''
  };

  var index = 0; // zero-based step index
  var submitting = false;

  /* -------------------------------------------------------------
     Persistence — sessionStorage under the shared key
     ------------------------------------------------------------- */

  function save() {
    try {
      sessionStorage.setItem(Logic.STORAGE_KEY, JSON.stringify({
        name: answers.name,
        weightKg: answers.weightKg,
        dob: answers.dob,
        breed: answers.breed,
        concerns: answers.concerns,
        email: answers.email,
        step: index
      }));
    } catch (e) { /* private mode, quota — the quiz still works in memory */ }
  }

  function restore() {
    var raw;
    try {
      raw = sessionStorage.getItem(Logic.STORAGE_KEY);
    } catch (e) { return; }
    if (!raw) return;

    var saved;
    try {
      saved = JSON.parse(raw);
    } catch (e) { return; }
    if (!saved || typeof saved !== 'object') return;

    answers.name = String(saved.name || '').slice(0, 40);
    answers.breed = String(saved.breed || '').slice(0, 60);
    answers.email = String(saved.email || '').slice(0, 120);
    answers.dob = /^\d{4}-\d{2}-\d{2}$/.test(saved.dob || '') ? saved.dob : '';

    var kg = parseInt(saved.weightKg, 10);
    if (isFinite(kg) && kg >= config.weightMin && kg <= config.weightMax) answers.weightKg = kg;

    if (Array.isArray(saved.concerns)) {
      answers.concerns = saved.concerns.filter(function (id) { return !!Logic.concernById(id); });
    }

    var step = parseInt(saved.step, 10);
    if (isFinite(step) && step > 0 && step < config.total) index = step;
  }

  /* -------------------------------------------------------------
     Rendering answers back into the fields
     ------------------------------------------------------------- */

  function pad(n) {
    return n < 10 ? '0' + n : String(n);
  }

  function renderWeight() {
    var kg = answers.weightKg;
    var text = kg + ' ' + config.unitKg + '  |  ' + Logic.toPounds(kg) + ' ' + config.unitLb;
    if (el.readout) el.readout.textContent = text;
    if (el.weight) {
      el.weight.value = String(kg);
      el.weight.setAttribute('aria-valuetext', text);
    }

    // Four silhouettes, four weight bands. Absolute kilograms rather than
    // proportions of the range: a 10kg dog is a small dog whatever the slider
    // is set to, and the four drawings are toy / small / large / giant.
    var band = 3;
    if (kg <= 10) band = 0;
    else if (kg <= 25) band = 1;
    else if (kg <= 40) band = 2;

    el.dogs.forEach(function (node, i) {
      node.classList.toggle('is-active', i === band);
    });
  }

  function renderDob() {
    if (!el.day || !el.month || !el.year) return;
    var parts = answers.dob.split('-');
    if (parts.length !== 3) return;
    el.year.value = String(parseInt(parts[0], 10));
    el.month.value = String(parseInt(parts[1], 10));
    el.day.value = String(parseInt(parts[2], 10));
  }

  function renderChips() {
    if (!el.chips) return;
    var frag = document.createDocumentFragment();

    Logic.CONCERNS.forEach(function (concern) {
      var chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'rumi-quizapp__chip';
      chip.textContent = concern.label;
      chip.dataset.concern = concern.id;
      chip.setAttribute('aria-pressed', answers.concerns.indexOf(concern.id) !== -1 ? 'true' : 'false');
      frag.appendChild(chip);
    });

    el.chips.innerHTML = '';
    el.chips.appendChild(frag);
  }

  function toggleConcern(chip) {
    var id = chip.dataset.concern;
    var at = answers.concerns.indexOf(id);
    if (at === -1) answers.concerns.push(id); else answers.concerns.splice(at, 1);
    chip.setAttribute('aria-pressed', at === -1 ? 'true' : 'false');
    save();
    syncNext();
  }

  /* -------------------------------------------------------------
     Validation, one rule per step
     ------------------------------------------------------------- */

  function validDate(y, m, d) {
    var date = new Date(y, m - 1, d);
    if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return false;
    return date <= new Date();
  }

  function readDob() {
    if (!el.day || !el.month || !el.year) return '';
    var d = parseInt(el.day.value, 10);
    var m = parseInt(el.month.value, 10);
    var y = parseInt(el.year.value, 10);
    if (!d || !m || !y) return '';
    if (!validDate(y, m, d)) return '';
    return y + '-' + pad(m) + '-' + pad(d);
  }

  function isValid(i) {
    switch (i) {
      case 0: return answers.name.trim().length > 0;
      case 1: return true; // the slider always holds a usable value
      case 2: return answers.dob !== '';
      case 3: return answers.breed.trim().length > 0;
      case 4: return !config.concernsRequired || answers.concerns.length > 0;
      case 5: return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(answers.email.trim());
      default: return false;
    }
  }

  function showError(i, show) {
    var node = el.steps[i] && el.steps[i].querySelector('[data-quiz-error]');
    if (node) node.hidden = !show;
  }

  function syncNext() {
    if (!el.next) return;
    el.next.disabled = submitting || !isValid(index);
    if (isValid(index)) showError(index, false);
  }

  /* -------------------------------------------------------------
     The state machine
     ------------------------------------------------------------- */

  function render(direction) {
    el.steps.forEach(function (step, i) {
      var active = i === index;
      step.hidden = !active;
      step.classList.toggle('is-active', active);
      step.classList.remove('is-forward', 'is-back');
      if (active && direction && !reduceMotion) {
        // Restart the animation even when re-entering the same class.
        void step.offsetWidth;
        step.classList.add(direction > 0 ? 'is-forward' : 'is-back');
      }
    });

    // The bar takes the exact fraction (the frames step it in true sixths of
    // the track); only the label rounds.
    var fraction = (index + 1) / config.total;
    if (el.fill) el.fill.style.width = fraction * 100 + '%';
    if (el.pct) el.pct.textContent = Math.round(fraction * 100) + '%';

    if (el.back) el.back.hidden = index === 0;
    if (el.nextLabel && el.next) {
      el.nextLabel.textContent = index === config.total - 1
        ? el.next.dataset.submitLabel
        : el.next.dataset.nextLabel;
    }

    syncNext();
    save();
  }

  function focusStep() {
    var heading = el.steps[index] && el.steps[index].querySelector('.rumi-quizapp__q');
    if (heading) heading.focus({ preventScroll: false });

    if (el.live) {
      var heard = config.announce
        .replace('[step]', String(index + 1))
        .replace('[total]', String(config.total));
      el.live.textContent = heard + '. ' + (heading ? heading.textContent.trim() : '');
    }
  }

  /* The prototype crossfades between questions: the current one goes out
     before the next comes in. Reduced motion skips straight to the swap so
     nobody waits on an animation they asked not to see. */
  var FADE_OUT_MS = 200;
  var leaving = null;

  function goTo(next, direction) {
    if (next < 0 || next >= config.total) return;

    function swap() {
      leaving = null;
      root.removeAttribute('data-quiz-phase');
      index = next;
      render(direction);
      focusStep();
    }

    if (reduceMotion) {
      swap();
      return;
    }

    /* A second tap mid-fade must not queue a second swap. */
    if (leaving) clearTimeout(leaving);
    root.setAttribute('data-quiz-phase', 'leaving');
    leaving = setTimeout(swap, FADE_OUT_MS);
  }

  /* -------------------------------------------------------------
     Klaviyo — best effort, never blocking
     ------------------------------------------------------------- */

  /*
    Two calls, because they answer different questions.

    /client/subscriptions/ needs a list to subscribe to. Without one it does
    nothing — which is how six questions' worth of answers were being thrown
    away on every completed quiz while the list id sat empty in theme
    settings. So the profile is always written first, and the subscription
    only runs when there is somewhere to subscribe to.
  */
  function quizProperties(handles) {
    return {
      dog_name: answers.name,
      dog_weight_kg: answers.weightKg,
      dog_date_of_birth: answers.dob,
      dog_breed: answers.breed,
      dog_concerns: answers.concerns,
      recommended_products: handles
    };
  }

  function postToKlaviyo(path, payload) {
    try {
      fetch(
        'https://a.klaviyo.com/client/' + path + '/?company_id=' + encodeURIComponent(config.klaviyoKey),
        {
          method: 'POST',
          headers: { revision: '2024-10-15', 'content-type': 'application/json' },
          body: JSON.stringify(payload),
          keepalive: true,
          mode: 'cors'
        }
      ).catch(function () { /* the plan still ships; the redirect must not wait */ });
    } catch (e) { /* no fetch, or blocked — carry on */ }
  }

  function pushToKlaviyo(handles) {
    if (!config.klaviyoKey) return;

    var email = answers.email.trim();
    if (!email) return;

    /* Always: create or update the profile, so the answers survive even with
       no list configured. */
    postToKlaviyo('profiles', {
      data: {
        type: 'profile',
        attributes: {
          email: email,
          properties: quizProperties(handles)
        }
      }
    });

    if (!config.klaviyoList) return;

    postToKlaviyo('subscriptions', {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'RUMI dog quiz',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                properties: quizProperties(handles)
              }
            }
          }
        },
        relationships: {
          list: { data: { type: 'list', id: config.klaviyoList } }
        }
      }
    });
  }

  function submit() {
    if (submitting) return;
    submitting = true;
    syncNext();

    var bundle = Logic.recommend(answers.concerns);
    pushToKlaviyo(bundle.handles);
    save();

    // Email is deliberately absent from the query string.
    var query = Logic.encodeState(answers);
    var url = config.resultsUrl.split('?')[0];
    window.location.assign(query ? url + '?' + query : url);
  }

  /* -------------------------------------------------------------
     Wiring
     ------------------------------------------------------------- */

  function bindText(input, key, max) {
    if (!input) return;
    input.addEventListener('input', function () {
      answers[key] = input.value.slice(0, max);
      save();
      syncNext();
    });
  }

  bindText(el.name, 'name', 40);
  bindText(el.breed, 'breed', 60);
  bindText(el.email, 'email', 120);

  if (el.weight) {
    el.weight.addEventListener('input', function () {
      var kg = parseInt(el.weight.value, 10);
      answers.weightKg = isFinite(kg) ? kg : config.weightDefault;
      renderWeight();
      save();
    });
  }

  // Next stays disabled until a step is valid, so these blur/change hints are
  // the only way a half-finished answer explains itself.
  if (el.email) {
    el.email.addEventListener('blur', function () {
      showError(5, el.email.value.trim() !== '' && !isValid(5));
    });
  }

  [el.day, el.month, el.year].forEach(function (select) {
    if (!select) return;
    select.addEventListener('change', function () {
      answers.dob = readDob();
      var complete = !!(el.day.value && el.month.value && el.year.value);
      showError(2, complete && answers.dob === '');
      save();
      syncNext();
    });
  });

  if (el.chips) {
    el.chips.addEventListener('click', function (event) {
      var chip = event.target.closest('[data-concern]');
      if (chip) toggleConcern(chip);
    });
  }

  if (el.back) {
    el.back.addEventListener('click', function () {
      goTo(index - 1, -1);
    });
  }

  if (el.form) {
    el.form.addEventListener('submit', function (event) {
      event.preventDefault();
      if (!isValid(index)) {
        showError(index, true);
        return;
      }
      if (index === config.total - 1) submit(); else goTo(index + 1, 1);
    });
  }

  /* -------------------------------------------------------------
     Boot
     ------------------------------------------------------------- */

  restore();
  if (el.name) el.name.value = answers.name;
  if (el.breed) el.breed.value = answers.breed;
  if (el.email) el.email.value = answers.email;
  renderChips();
  renderDob();
  renderWeight();
  render(0);

  /* Shopify's privacy banner is position:fixed at the bottom of the viewport.
     On a scrolling page it just overlays content you can scroll past; on the
     quiz, which fills the viewport with Next pinned near the bottom, it sits
     directly on top of the button — elementFromPoint at the button's centre
     returns the banner. A first-time visitor, which is everyone taking the
     quiz, cannot advance past step 1 until they dismiss it.

     Reserve exactly the banner's height while it is on screen, and give it
     back the moment it goes. Measured rather than hardcoded: the banner's
     height varies with viewport and copy. */
  (function reserveConsentSpace() {
    var shell = root;
    var applied = 0;

    function measure() {
      var banner = document.querySelector('#shopify-pc__banner');
      var h = 0;
      if (banner) {
        var cs = window.getComputedStyle(banner);
        if (cs.display !== 'none' && cs.visibility !== 'hidden' && cs.position === 'fixed') {
          h = Math.round(banner.getBoundingClientRect().height);
        }
      }
      if (h === applied) return;
      applied = h;
      shell.style.setProperty('--rumi-quiz-consent', h + 'px');
      shell.classList.toggle('has-consent-banner', h > 0);
    }

    measure();
    if (typeof MutationObserver === 'function') {
      new MutationObserver(measure).observe(document.body, { childList: true, subtree: false });
    }
    window.addEventListener('resize', measure);
    // the banner is injected asynchronously, so re-check for a short while
    var ticks = 0;
    var poll = setInterval(function () {
      measure();
      if (++ticks > 20) clearInterval(poll);
    }, 500);
  })();

})();
